import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home, Bug, AlertCircle } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  resetKeys?: Array<string | number>;
  resetOnPropsChange?: boolean;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorId: string;
  errorBoundaryType: 'render' | 'async' | 'event' | 'unknown';
}

class ErrorBoundary extends Component<Props, State> {
  private retryCount = 0;
  private maxRetries = 3;
  private resetTimeoutRef: NodeJS.Timeout | null = null;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: '',
      errorBoundaryType: 'unknown',
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    const errorId = `error-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    // Determine error type based on error properties
    let errorBoundaryType: State['errorBoundaryType'] = 'unknown';
    if (error.name === 'ChunkLoadError' || error.message.includes('Loading chunk')) {
      errorBoundaryType = 'async';
    } else if (error.stack?.includes('componentDidUpdate') || error.stack?.includes('render')) {
      errorBoundaryType = 'render';
    } else if (error.name === 'TypeError' && error.message.includes('event')) {
      errorBoundaryType = 'event';
    }

    return {
      hasError: true,
      error,
      errorId,
      errorBoundaryType,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({
      error,
      errorInfo,
    });

    const errorDetails = {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      errorId: this.state.errorId,
      retryCount: this.retryCount,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      errorType: this.state.errorBoundaryType,
    };

    // Log comprehensive error details

    // Call custom error handler if provided
    if (this.props.onError) {
      try {
        this.props.onError(error, errorInfo);
      } catch (handlerError) {
      }
    }

    // Clear any potentially corrupted data
    this.clearCorruptedData();

    // Attempt automatic recovery for certain error types
    this.attemptAutoRecovery(error, errorDetails);
  }

  componentDidUpdate(prevProps: Props) {
    const { resetKeys, resetOnPropsChange } = this.props;
    const { hasError } = this.state;

    // Auto-reset if resetKeys change
    if (hasError && resetKeys && prevProps.resetKeys) {
      const hasResetKeyChanged = resetKeys.some(
        (resetKey, idx) => prevProps.resetKeys![idx] !== resetKey
      );
      if (hasResetKeyChanged) {
        this.resetErrorBoundary();
      }
    }

    // Auto-reset if any props change (optional)
    if (hasError && resetOnPropsChange && prevProps !== this.props) {
      this.resetErrorBoundary();
    }
  }

  componentWillUnmount() {
    if (this.resetTimeoutRef) {
      clearTimeout(this.resetTimeoutRef);
    }
  }

  private attemptAutoRecovery = (error: Error, errorDetails: any) => {
    // Auto-retry for chunk loading errors (common in production)
    if (errorDetails.errorType === 'async' || error.name === 'ChunkLoadError') {
      this.resetTimeoutRef = setTimeout(() => {
        window.location.reload();
      }, 2000);
      return;
    }

    // Auto-retry for render errors if retry count is low
    if (errorDetails.errorType === 'render' && this.retryCount < 2) {
      this.resetTimeoutRef = setTimeout(() => {
        this.handleRetry();
      }, 1000);
    }
  };

  private clearCorruptedData = () => {
    try {
      // Clear game state data that might be causing issues
      const today = new Date();
      const year = today.getFullYear();
      const month = (today.getMonth() + 1).toString().padStart(2, '0');
      const day = today.getDate().toString().padStart(2, '0');
      const todayKey = `pathways-game-${year}-${month}-${day}`;
      
      // Clear potentially corrupted localStorage data
      const keysToRemove = [
        todayKey,
        'pathways-settings',
        'pathways-user-preferences',
        'pathways-auth-state',
      ];
      
      keysToRemove.forEach(key => {
        try {
          localStorage.removeItem(key);
        } catch (e) {
        }
      });

      // Clear sessionStorage as well
      try {
        sessionStorage.clear();
      } catch (e) {
      }

    } catch (e) {
    }
  };

  private resetErrorBoundary = () => {
    if (this.resetTimeoutRef) {
      clearTimeout(this.resetTimeoutRef);
      this.resetTimeoutRef = null;
    }

    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: '',
      errorBoundaryType: 'unknown',
    });
  };

  private handleRetry = () => {
    if (this.retryCount < this.maxRetries) {
      this.retryCount++;
      this.resetErrorBoundary();
    } else {
    }
  };

  private handleReset = () => {
    // Clear all application data and reload
    this.clearCorruptedData();
    
    // Clear browser cache if possible
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => {
          caches.delete(name);
        });
      });
    }

    window.location.reload();
  };

  private getErrorTitle = () => {
    const { errorBoundaryType } = this.state;
    switch (errorBoundaryType) {
      case 'async':
        return 'Loading Error';
      case 'render':
        return 'Display Error';
      case 'event':
        return 'Interaction Error';
      default:
        return 'Application Error';
    }
  };

  private getErrorDescription = () => {
    const { errorBoundaryType } = this.state;
    switch (errorBoundaryType) {
      case 'async':
        return 'Failed to load a required resource. This might be due to a network issue or an outdated cache.';
      case 'render':
        return 'Something went wrong while displaying the game. This might be due to corrupted data.';
      case 'event':
        return 'An error occurred while processing your interaction.';
      default:
        return 'The application encountered an unexpected error.';
    }
  };

  private renderErrorDetails = () => {
    if (!this.state.error || !this.state.errorInfo) return null;

    return (
      <details className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
        <summary className="font-semibold text-red-800 cursor-pointer hover:text-red-900 flex items-center gap-2">
          <Bug size={16} />
          Technical Details (Click to expand)
        </summary>
        <div className="mt-3 space-y-3">
          <div className="text-sm">
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div><strong>Error ID:</strong> {this.state.errorId}</div>
              <div><strong>Type:</strong> {this.state.errorBoundaryType}</div>
              <div><strong>Retry Count:</strong> {this.retryCount}/{this.maxRetries}</div>
              <div><strong>Timestamp:</strong> {new Date().toLocaleTimeString()}</div>
            </div>
          </div>
          
          <div className="text-sm">
            <strong className="text-red-800">Error Message:</strong>
            <div className="mt-1 p-2 bg-red-100 rounded font-mono text-xs">
              {this.state.error.message}
            </div>
          </div>
          
          <div className="text-sm">
            <strong className="text-red-800">Stack Trace:</strong>
            <pre className="mt-1 text-xs overflow-auto max-h-32 bg-red-100 p-2 rounded font-mono whitespace-pre-wrap">
              {this.state.error.stack}
            </pre>
          </div>
          
          <div className="text-sm">
            <strong className="text-red-800">Component Stack:</strong>
            <pre className="mt-1 text-xs overflow-auto max-h-32 bg-red-100 p-2 rounded font-mono whitespace-pre-wrap">
              {this.state.errorInfo.componentStack}
            </pre>
          </div>
        </div>
      </details>
    );
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default comprehensive error UI
      return (
        <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
          <div className="max-w-lg w-full bg-white rounded-2xl shadow-xl border border-red-100 overflow-hidden">
            {/* Header */}
            <div className="bg-red-500 text-white p-6 text-center">
              <AlertTriangle size={48} className="mx-auto mb-3" />
              <h1 className="text-xl font-bold">{this.getErrorTitle()}</h1>
              <p className="text-red-100 mt-1">
                {this.getErrorDescription()}
              </p>
            </div>

            {/* Content */}
            <div className="p-6">
              {/* Auto-recovery message */}
              {this.state.errorBoundaryType === 'async' && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3">
                  <AlertCircle size={20} className="text-blue-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium">Auto-recovery in progress...</p>
                    <p>The page will reload automatically to fix this issue.</p>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="space-y-3">
                {this.retryCount < this.maxRetries && this.state.errorBoundaryType !== 'async' && (
                  <button
                    onClick={this.handleRetry}
                    className="w-full bg-blue-600 text-white py-3 px-4 rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-blue-700 transition-colors"
                  >
                    <RefreshCw size={18} />
                    Try Again ({this.maxRetries - this.retryCount} attempts left)
                  </button>
                )}

                <button
                  onClick={this.handleReset}
                  className="w-full bg-slate-600 text-white py-3 px-4 rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-slate-700 transition-colors"
                >
                  <Home size={18} />
                  Reset & Reload Game
                </button>
              </div>

              {/* Error Details (Development Only) */}
              {process.env.NODE_ENV === 'development' && this.renderErrorDetails()}

              {/* User Support Message */}
              <div className="mt-6 p-4 bg-slate-50 border border-slate-200 rounded-lg">
                <p className="text-sm text-slate-600">
                  <strong>Still having issues?</strong> Try refreshing your browser, clearing your browser cache, 
                  or using an incognito/private browsing window.
                </p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;